import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Typography,
  Card,
  CardMedia,
  CardActions,
  Grid,
  Stack,
} from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import StepWizard from "../../components/stepWizard/StepWizard";
import { useSelector } from "react-redux";
import {
  getMotor,
  getModelList,
  getCurrentStep,
  getOrderDetails,
  getSelectedModal,
  getLookupsDetails,
  getSeriesList,
} from "../../store/slices/BoatOrdering/BoatOrderingSelector";
import { apiStore } from "../../api/ApiFactory";
import BoatSlider from "../../components/slider/Slider";
import { IOptions, IBreadCrumb, IErrorMessage } from "../../models/BoatOrdering";
import BoatOrderingSummary from "./BoatOrderingSummary";
import BoatOrderingBreadCrumb from "./BoatOrderingBreadCrumb";
import useScroll from "../../helpers/utils/useScroll";
import ConfirmationModal from "./ConfirmationModal";
import CurrencyFormat from "../../components/currencyFormat/CurrencyFormat";
import PageLoader from "../../components/loader/PageLoader";
import { enumOrderType, calculatePriceByType } from "../../helpers/utils/utils";
import { selectAuthUser } from "../../store/slices/Auth/AuthSelector";
import SubmitIndex from "./SubmitForm/SubmitIndex";
import { useAppSelector } from "../../store/hooks/hook";
import { NavMode } from "../../helpers/enums/Layout";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ISeries } from "../../models/Configuration";
import stockimage from "../../_assets/images/Sea-Pro-Logo-Header.png";
import { HasPermission } from "../../services/AuthService";
import { Permissions, Roles } from "../../helpers/enums/AuthConstants";
import { IAuthUser } from "../../models/Auth";
import SmartText from "../../components/smartText/SmartText";
import MainModal from "../../components/modal/MainModal";
import PrintModelModal from "../print/Model/PrintModelModal";
import PrintModel from "../print/Model/PrintModel";
import { getDealer } from "../../store/slices/Dealer/DealerSelector";
import CSVExportModel from "../print/Model/CSVExportModel";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

const BoatOrderingModel = () => {
  const { navMode, msrp, httpload } = useAppSelector((state) => ({
    navMode: state.reducer.Layout.navMode,
    msrp: state.reducer.Boat.selectedSeriesMsrp,
    httpload: state.reducer.Layout.loading
  }));

  const params = useParams();
  const motor = useSelector(getMotor);
  const details = useSelector(getOrderDetails);
  const modelList = useSelector(getModelList);
  const lookups = useSelector(getLookupsDetails);
  const seriesList = useSelector(getSeriesList);
  const [stepHeading, setStepHeading] = useState<String[]>([]);
  const [currentOptions, setCurrentOptions] = useState<IOptions[]>([]);
  const [openModal, setOpenModal] = useState<any>(false);
  const [breadCrumb, setBreadCrumb] = useState<IBreadCrumb>({
    price: 0,
    name: "",
    modelYear: "",
    priceType: navMode === NavMode.LEADS ? "map" : "dealerCost",
    msrp: 0.0,
  });
  const [loading, setLoading] = useState<Boolean>(true);
  const [isView, setIsView] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [selectedSeriesInfo, setSelectedSeriesInfo] = useState<ISeries>();
  const selectedModal = useSelector(getSelectedModal);
  const currentStep = useSelector(getCurrentStep);
  const auth: IAuthUser | null = useSelector(selectAuthUser);
  const dealerDetails = useSelector(getDealer);

  const [errorMessage, setErrorMessage] = useState<IErrorMessage | undefined>(undefined);

  const [open, setPrintModal] = useState(false);
  const [modelForPrint, setModelForPrint] = useState<any>(null);
  const [typeForPrint, setTypeForPrint] = useState<any>(null);
  const [printSheet, setPrintSheet] = useState<boolean>(false);
  const [exportSheet, setExportSheet] = useState<boolean>(false);
  const lastPathRef = useRef<string | null>(null);
  const lastSelectedModel = useRef<string | null>(null);
  const [displayConfigSection, setDisplayConfigSection] = useState<boolean>(false);
  const [filteredModelList, setFilteredModelList] = useState<any[]>([]);

  const setSelectedModal = (id: number) => {
    apiStore.boatOrderingApi.setSelectedModal({ id: id, isView: isView });
  };

  const setSelectedConfig = (key: string) => {
    const list = modelList.filter((item: { importConfigName: string }) => item.importConfigName == key);
    setFilteredModelList(list);
    setDisplayConfigSection(false);
  }

  let navigate = useNavigate();

  // Changed default cost dropdown value if logged in user is dealer
  useEffect(() => {
    const hasPermission = HasPermission(auth?.user, Permissions.UseDealerCost,Roles.DEALER);

    //It is possible to be logged in and on the leads page.
    if (navMode !== NavMode.LEADS && auth?.user?.role === Roles.DEALER) {
      var newCostType = dealerDetails?.defaultCostType ?? "dealerCost";

      if (!hasPermission && newCostType === "dealerCost") {
        newCostType = "msrp";
      }

      setBreadCrumb({
        ...breadCrumb,
        priceType: newCostType,
      });
    }
  }, [dealerDetails]);

  useEffect(() => {
    initializeComponent();
  }, []);

  const handleGetModelList = (seriesId: any, modelId: any, mode: any) => {
    apiStore.boatOrderingApi
      .getModelDefinition({
        boatSeriesId: seriesId,
        isConsumer: navMode === NavMode.LEADS,
        mode: mode,
        modelId: modelId
      })
      .then((listRes: any) => {
        setLoading(false);
      })
      .catch((err) => {
        console.log("Model list error", err);
      });

    if (navMode !== NavMode.LEADS && auth?.user?.role === Roles.DEALER) {
      apiStore.dealerApi.getDealer({
        dealerId: auth?.user?.dealerId,
      });
    }
  };

  const initializeComponent = async () => {
    apiStore.boatOrderingApi.resetBoatStates();

    // Use existing state if present, otherwise fetch and use what we get back
    let series = seriesList;
    if(seriesList.length === 0) {
      series = await apiStore.boatOrderingApi.getSeriesList();
    }

    apiStore.boatOrderingApi.getLookups({
      isLeads: navMode === NavMode.LEADS,
    });

    if (
      window.location.pathname?.includes("/boat/order/view/") ||
      window.location.pathname?.includes("/boat/order/clone/")
    ) {

      apiStore.orderManagementApi
        .getOrderDetails({ OrderNum: Number(params.id) })
        .then((response: any) => {

          if (series.length > 0 && response.seriesId !== undefined) {
            const data = series.find((series: any) => series.id == response.seriesId);
            setSelectedSeriesInfo(data);
          }

          let mode = "";

          let view = response.salesOrderStatus === "DO" || response.salesOrderStatus === "Sched" || response.salesOrderStatus === "Quote"
              ? false : true;

          if ((auth?.user?.role === Roles.SALESREP && response.salesOrderStatus !== "Quote") || response.modelArchived === true) {
            view = true;
          }

          const typestr = response.salesOrderStatus === "Quote" ? "quote" : "order";
          if (window.location.pathname?.includes("/boat/order/clone/")) {

            if(response.modelArchived === true || response.allowModelAccess === false) {
              const error: IErrorMessage = {
                title: "Unable to clone " + typestr + " " + response.orderNumber,
                subtitle: "The model " + response.revisionNumber + " " + response.modelDisplayName + " is not configured to allow cloning."
              }

              setErrorMessage(error);
            }
            else {
              //Order can be cloned as long as the model is not archived.
              view = false;
              mode = "clone";
            }
          }
          else if(response.allowModelAccess === false) {

            const actionstr = view ? "view" : "edit";

            const error: IErrorMessage = {
              title: "Unable to " + actionstr + " " + typestr + " " + response.orderNumber,
              subtitle: "The model " + response.revisionNumber + " " + response.modelDisplayName + " is not configured to allow " + actionstr + "ing."
            }

            setErrorMessage(error);
          }
          else {
            mode = view  ? "viewonly" : "edit";
          } 
          
          setIsView(view);

          if(mode != "") {
            handleGetModelList(response.seriesId, response.modelId, mode);
          }
          else {
            setLoading(false);
          }
          
        })
        .catch((error) => {
          console.log("order details error", error);
        });

    } 
    else {

      //Only display boat slider on new orders.
      setIsNew(true);

      if (series.length > 0) {
        const data = series.find((series: any) => series.id == params?.id);

        pushGTMEvent(data?.name);

        setSelectedSeriesInfo(data);
        setDisplayConfigSection(true);
        handleGetModelList(data?.id, null, "create");

      }
    } 
  }

  const pushGTMEvent = (pageTitle: string) => {
    //Log the event in Google Tag Manager for tracking.
    if (navMode === NavMode.LEADS) {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
            event: 'manual_page_view',
            page_title: pageTitle,
            page_path: window.location.pathname,
            page_location: window.location.href,
        });
    }
  }

  useEffect(() => {

    if ((details?.selectedOptions?.rdoSeriesModel || details?.selectedOptions?.cmbSeriesModel) && modelList?.length) {

      modelList.forEach((model: any, index: number) => {
        if (model.id === details?.modelId) {
          setSelectedModal(index);
          setSelectedConfig(model?.importConfigName);
        }
      });
      if (details?.enginePartNum) {
        apiStore.boatOrderingApi.setMotor(details?.enginePartNum);
      }

      let dealer = "";
      lookups?.dealerList?.forEach((dealeritem: any) => {
        dealeritem?.locations?.forEach((location: any) => {
          if (location.customerID == details.customerId) {
            dealer = dealeritem.id;
          }
        });
      });

      let orderType = Object.entries(enumOrderType)?.filter(
        (el) => el[0] === details?.salesOrderStatus
      )[0][1];

      apiStore.boatOrderingApi.setBoatForm({
        dealer: dealer,
        location: details?.customerId,
        orderType: orderType,
        delivery: details?.requestedDelivery,
        term: details?.terms,
        freight: JSON.parse(JSON.stringify(details?.shipToCode)),
        customerPO: details?.poNum,
        customerNotes: details?.comments,
        showDate: details?.showDate,
        retailCustomer: details?.retailCustomer,
        trailer: details?.trailer,
        trailerType: details?.trailerType,
      });
    }

  }, [details?.selectedOptions, details?.revisionNumber, modelList, lookups]); // for existing order
  
  useEffect(() => {
    let mounted = true;
    if (mounted) {

      if (selectedModal?.name && currentStep < selectedModal?.sections?.length) {
          const { options }: any = selectedModal?.sections[currentStep];
          setCurrentOptions(options);
      }
      useScroll.scrollTop("app-content");
    }
    return () => {
      mounted = false;
    };
  }, [currentStep]);
  

  useEffect(() => {
    if (selectedModal?.name) {
      //Log the event in Google Tag Manager for tracking.
      if ((lastPathRef.current !== window.location.pathname || lastSelectedModel.current !== selectedModal?.name) && navMode === NavMode.LEADS) {
          lastPathRef.current = window.location.pathname;
          lastSelectedModel.current = selectedModal?.name;
          pushGTMEvent(selectedModal?.name);
      }

      let steps: String[] = [];
      const list: any[] = modelList.filter(
        (item: { id: any }) => item.id == selectedModal.id
      );

      const { options }: any = selectedModal?.sections[currentStep];

      setBreadCrumb({
        ...breadCrumb,
        price: selectedModal?.totalPrice,
        name: selectedModal?.name,
        modelYear: list[0].yearDisplayLabel,
        msrp: msrp,
      });

      setCurrentOptions(options);

      selectedModal?.sections?.forEach((el: any) => {
        steps.push(el.name);
      });

      setStepHeading([...steps]);
    }
  }, [selectedModal]);

  const handleConfirmation = (modelId: any): void => {
    const modelIndex = modelList
      .findIndex((m: any) => m.id === modelId)
      ?.toString()

    setOpenModal(modelIndex);
  };

  const handleViewDetails = (url: string) => {
    window.open(url, "_blank");
  };

  const OpenPrintModal = (model: any) => {
    setModelForPrint(model);
    setTypeForPrint(null);
    setPrintModal(true);
  };

  const closePrintModal = () => {
    setModelForPrint(null);
    setTypeForPrint(null);
    setPrintModal(false);
  };

  const handlePrint = (type: string, format : string) => {
    setTypeForPrint(type);
    setPrintModal(false);
    format === 'print' ? setPrintSheet(true) : setExportSheet(true);
  };

  useEffect(() => {
    if (printSheet === true) {
      window.print();
      setPrintSheet(false);
    }
  }, [printSheet]);

  const handleExportComplete = () => {
      setExportSheet(false);
  };

  const handleClose = () => {
    navigate(-1);
  }

  //It is possible that http requests are complete but the page is not done loading.
  if (loading) {

    if(httpload) {
      return <></>
    }
    else {
      return <PageLoader loading={true} showTitle hideImg={false} />
    }

  }

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    arrows: false,
    responsive: [
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 400,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <div>
      {printSheet && <PrintModel model={modelForPrint} type={typeForPrint} series={selectedSeriesInfo} dealer={dealerDetails} />}
      {exportSheet && <CSVExportModel data={modelForPrint} type={typeForPrint} series={selectedSeriesInfo} dealer={dealerDetails} onExportComplete={handleExportComplete} />}

      <MainModal
        open={open}
        handleClose={closePrintModal}
        customStyle={{ maxWidth: "600px" }}
      >
        <PrintModelModal
          model={modelForPrint}
          handleClose={closePrintModal}
          handlePrint={handlePrint}
          label="Model Options"
        />
      </MainModal>

      <ConfirmationModal open={openModal}
        handleClickYes={() => {
          setSelectedModal(openModal);
          setOpenModal(false);
        }}
        handleClickCancel={() => setOpenModal(false)}
        title="Are you sure want to leave this model?"
        description="If you click Yes, options selected will be lost."
      />

      {errorMessage &&  (
        <>
          <Stack spacing={3} sx={{justifyContent: "center", alignItems: "center"}}>
            <Grid>
              <Typography variant="h6" align="center">
                {errorMessage.title}
              </Typography>
              <Typography variant="body2" align="center">
                {errorMessage.subtitle}
              </Typography>
            </Grid>
            
             <Button variant="contained" onClick={handleClose}>{" "}Back{" "}</Button>
          </Stack>
        </>
      )}

      {!selectedModal && !isView && isNew && (
        <>
          <Grid container mb={2} spacing={4} className="slider-container">
            {selectedSeriesInfo && (
              <>
                <Grid item md={4} xl={3} sm={12}>
                  <Slider {...sliderSettings} className="img-slider">
                    {selectedSeriesInfo?.sliderMedia?.map(
                      (sliderImage, index) => (
                        <div key={index}>
                          <img src={sliderImage.mediaUrl} alt="slider-img" />
                        </div>
                      )
                    )}
                  </Slider>
                </Grid>
                <Grid item md={5} lg={6}>
                  <Typography mb={1} className="series-header">
                    {selectedSeriesInfo?.name}
                  </Typography>
                  <Typography mb={1}>
                    {selectedSeriesInfo?.modelCount}+ Models
                  </Typography>
                  <Typography className="desc">
                    <SmartText
                      text={selectedSeriesInfo.description}
                      char={400}
                    />
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
          <div className="config-slider-header">
            {!displayConfigSection && (
              <Button
                variant="text"
                className="config-slider-back"
                startIcon={<ChevronLeftIcon />}
                onClick={() => setDisplayConfigSection(true)}
              >
                Back to Model Types
              </Button>
            )}
          </div>
          <div className="config-slider-viewport">
            <div
              className={`config-slider-track ${
                displayConfigSection ? "show-config" : "show-models"
              }`}
            >
              <div className="config-slider-panel">
                <Grid
                  container
                  spacing={{ xs: 2, md: 3 }}
                  columns={{ xs: 4, sm: 8, md: 12 }}
                >
            {selectedSeriesInfo?.configurators?.map((config: any, index: any) => {

              const configModel = modelList?.find(
                (m: any) => m?.importConfigName === config?.importConfigName
              );
              const media = configModel?.layeredMedia?.find(
                (el: any) => el?.perspectiveName === "Side"
              );

              return (
                <Grid item xs={8} sm={4} md={3} key={index}>
                  <Card className="seriesCard">
                    <Typography
                      gutterBottom={false}
                      variant="h6"
                      component="div"
                      align="center"
                      className="seriesCardHeading"
                    >
                      {config?.name}
                    </Typography>
                    <CardMedia
                      component="img"
                      height="140"
                      image={media?.mediaUrl ?? stockimage}
                      alt={config?.name}
                      sx={{
                        objectFit: "contain",
                        //paddingLeft: 2,
                        //paddingRight: 2,
                      }}
                    />
                    <CardActions sx={{ justifyContent: "end" }}>
                      <Stack direction="row" spacing={2}>
                        <Button
                          size="small"
                          variant="contained"
                          className="configureCardButton"
                          onClick={() => setSelectedConfig(config?.importConfigName)}
                        >
                          Select Model
                        </Button>
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
                </Grid>
              </div>
              <div className="config-slider-panel">
                <Grid
                  container
                  spacing={{ xs: 2, md: 3 }}
                  columns={{ xs: 4, sm: 8, md: 12 }}
                >
            {filteredModelList?.map((model: any, index: any) => {

              const media = model?.layeredMedia?.find(
                (el: any) => el?.perspectiveName === "Side"
              );
              return (
                <Grid item xs={8} sm={4} md={3} key={index}>
                  <Card className="seriesCard">
                    <Typography
                      gutterBottom={false}
                      variant="h6"
                      component="div"
                      align="center"
                      className="seriesCardHeading"
                    >
                      {model?.yearDisplayLabel} - {model?.name}
                    </Typography>
                    <CardMedia
                      component="img"
                      height="140"
                      image={media?.mediaUrl ?? stockimage}
                      alt={model?.name}
                      sx={{
                        objectFit: "contain",
                        paddingLeft: 2,
                        paddingRight: 2,
                      }}
                    />
                    <Typography align="center">
                      <CurrencyFormat
                        value={ calculatePriceByType(
                          model.price,
                          breadCrumb?.priceType,
                          breadCrumb?.priceType == "msrp" ? msrp : dealerDetails?.salePriceMarkupModel
                        )}
                      />
                    </Typography>
                    <CardActions sx={{ justifyContent: "space-between" }}>
                      <Button
                        onClick={() => handleViewDetails(model?.detailUrl)}
                      >
                        View Details
                      </Button>
                      <Stack direction="row" spacing={2}>
                        {navMode !== NavMode.LEADS && (
                          <Button
                            size="small"
                            color="success"
                            variant="contained"
                            onClick={() => OpenPrintModal(model)}
                          >
                            Print
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          className="configureCardButton"
                          onClick={() =>
                            setSelectedModal(
                              modelList
                                .findIndex((m: any) => m.id === model.id)
                                ?.toString()
                            )
                          }
                        >
                          Configure
                        </Button>
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
                </Grid>
              </div>
            </div>
          </div>
        </>
      )}

      {selectedModal && (
        <div className="step-wrapper">
          {currentStep < selectedModal?.sections?.length && !isView && isNew && (
              <BoatSlider
                modelList={filteredModelList}
                selectedModal={selectedModal}
                priceType={breadCrumb?.priceType}
                setSelectedModal={handleConfirmation}
                msrp={breadCrumb.msrp}
              />
          )}

          <BoatOrderingBreadCrumb
            salePriceMarkupModel={dealerDetails?.salePriceMarkupModel}
            breadCrumb={breadCrumb}
            motorPrice={motor?.price}
            setBreadCrumb={(priceType) =>
              setBreadCrumb({ ...breadCrumb, priceType })
            }
          />
          {currentStep < selectedModal?.sections?.length ? (
            <StepWizard
              selectedModal={selectedModal}
              priceType={breadCrumb?.priceType}
              isView={isView}
              stepHeading={stepHeading}
              currentOptions={currentOptions}
              salePriceMarkupOption={dealerDetails?.salePriceMarkupOption}
              salePriceMarkupMotor={dealerDetails?.salePriceMarkupOption}
              msrp={breadCrumb.msrp}
            />
          ) : currentStep === selectedModal?.sections?.length ? (
            <BoatOrderingSummary
              model={selectedModal}
              priceType={breadCrumb?.priceType}
              msrp={breadCrumb.msrp}
            />
          ) : (
            <SubmitIndex isView={isView} />
          )}
        </div>
      )}
    </div>
  );
};
export default BoatOrderingModel;